import { Plus, Radio, X } from 'lucide-react';
import { useState } from 'react';
import { CreateSourceModal } from '../components/CreateSourceModal';
import { DeleteSourceModal } from '../components/DeleteSourceModal';
import { EditSourceModal } from '../components/EditSourceModal';
import { SourceDetailModal } from '../components/SourceDetailModal';
import { SourcesTable } from '../components/SourcesTable';
import { ToggleSourceModal } from '../components/ToggleSourceModal';
import { useSources } from '../hooks/useSources';
import type { Source } from '../types/sources.types';
import Protected from '@/shared/components/ahkinia/Protected/Protected';
import { SectionHeader } from '@/shared/components/ui/header';
import { Input } from '@/shared/components/ui/input';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { PERMISSIONS } from '@/shared/constants/permissions';

export default function SourcesPage() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [editingSourceId, setEditingSourceId] = useState<string | undefined>(undefined);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [togglingSource, setTogglingSource] = useState<Source | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingSource, setDeletingSource] = useState<{ id: string; name: string } | null>(null);

  const { sources, pagination } = useSources({
    page,
    pageSize,
    searchTerm: searchTerm || undefined,
    sortBy: 'name',
    sortAscending: true,
  });

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      setPage(1);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleViewDetail = (sourceId: string) => {
    setSelectedSourceId(sourceId);
    setDetailModalOpen(true);
  };

  const handleEdit = (sourceId: string) => {
    setEditingSourceId(sourceId);
    setEditModalOpen(true);
  };

  const handleToggle = (sourceId: string) => {
    const source = sources.find((s) => s.id === sourceId);
    if (source) setTogglingSource(source);
  };

  const handleDelete = (sourceId: string, sourceName: string) => {
    setDeletingSource({ id: sourceId, name: sourceName });
    setDeleteModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true);
  };

  const hasData = sources.length > 0;

  return (
    <div data-testid="sources-page" className="flex flex-col gap-6 p-6">
      <SectionHeader
        sectionIcon={<Radio />}
        title="Fuentes de información"
        subtitle="Gestión de fuentes de ingesta de datos de la plataforma"
      >
        <Protected permissions={PERMISSIONS.SOURCES_CREATE}>
          <UnifiedButton
            data-testid="create-source-btn"
            icon={<Plus />}
            onClick={handleOpenCreateModal}
          >
            Registrar fuente
          </UnifiedButton>
        </Protected>
      </SectionHeader>

      <div className="flex items-center gap-4">
        <Input
          data-testid="search-sources-input"
          placeholder="Buscar por nombre de la fuente..."
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          className="max-w-[300px]"
        />
        {searchTerm && (
          <UnifiedButton
            data-testid="clear-search-btn"
            variant="outline"
            onClick={handleClearSearch}
          >
            <X />
          </UnifiedButton>
        )}
      </div>

      {hasData ? (
        <SourcesTable
          data={sources}
          pagination={pagination}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onViewDetail={handleViewDetail}
          onEdit={handleEdit}
          onToggle={handleToggle}
          onDelete={handleDelete}
        />
      ) : (
        <div
          data-testid="sources-empty-state"
          className="flex flex-col items-center justify-center py-12 rounded-xl bg-background border-outline"
        >
          <Radio size={48} className="text-primary mb-4 opacity-50" />
          <p className="text-primary mb-4">
            No hay fuentes de información configuradas. Registra una nueva.
          </p>
          <Protected permissions={PERMISSIONS.SOURCES_CREATE}>
            <UnifiedButton
              data-testid="empty-create-source-btn"
              icon={<Plus />}
              onClick={handleOpenCreateModal}
            >
              Registrar fuente
            </UnifiedButton>
          </Protected>
        </div>
      )}

      <CreateSourceModal open={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} />
      <SourceDetailModal
        sourceId={selectedSourceId}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />
      <EditSourceModal
        sourceId={editingSourceId}
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingSourceId(undefined);
        }}
      />
      {togglingSource && (
        <ToggleSourceModal
          sourceId={togglingSource.id}
          sourceName={togglingSource.name}
          currentStatus={togglingSource.isEnabled}
          open={!!togglingSource}
          onClose={() => setTogglingSource(null)}
        />
      )}
      {deletingSource && (
        <DeleteSourceModal
          sourceId={deletingSource.id}
          sourceName={deletingSource.name}
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDeletingSource(null);
          }}
        />
      )}
    </div>
  );
}
